import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility,
  ExitToApp,
  Search,
  Refresh,
  CalendarToday,
  Person,
  Phone,
  Business,
  AccessTime,
  CheckCircle,
  Pending,
} from '@mui/icons-material';
import visitorService, { Visitor } from '../../services/visitorService';
import storeService from '../../services/storeService';
import { TableShimmer } from '../../components/common/Shimmer';
import ProfileAvatar from '../../components/common/ProfileAvatar';

const VisitorsPage: React.FC = () => {
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [filteredVisitors, setFilteredVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [stores, setStores] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    search: '',
    store_id: 'all',
    visit_date: new Date().toISOString().split('T')[0], // Today's date
    status: 'all', // all, active, exited
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    exited: 0,
  });

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    fetchVisitors();
  }, [filters.store_id, filters.visit_date]);

  useEffect(() => {
    applyFilters();
  }, [filters, visitors]);

  const fetchStores = async () => {
    try {
      const response = await storeService.getStores({ limit: 1000 });
      setStores(response.stores || []);
    } catch (err: any) {
      console.error('Failed to fetch stores:', err);
    }
  };

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      setError('');
      const params: any = {
        page: 1,
        limit: 100,
      };

      if (filters.store_id !== 'all') {
        params.store_id = filters.store_id;
      }

      if (filters.visit_date) {
        params.visit_date = filters.visit_date;
      }

      const response = await visitorService.getVisitors(params);
      setVisitors(response.visitors || []);
      
      // Calculate stats
      const statsData = {
        total: response.visitors?.length || 0,
        active: response.visitors?.filter((v: Visitor) => !v.exit_time).length || 0,
        exited: response.visitors?.filter((v: Visitor) => v.exit_time).length || 0,
      };
      setStats(statsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch visitors');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...visitors];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        visitor =>
          visitor.name.toLowerCase().includes(searchLower) ||
          visitor.contact_number.includes(searchLower) ||
          visitor.reason?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status !== 'all') {
      if (filters.status === 'active') {
        filtered = filtered.filter(visitor => !visitor.exit_time);
      } else if (filters.status === 'exited') {
        filtered = filtered.filter(visitor => visitor.exit_time);
      }
    }

    setFilteredVisitors(filtered);
  };

  const handleViewDetails = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setOpenDialog(true);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && visitors.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Visitors Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            View and manage visitor records
          </Typography>
        </Box>
        <TableShimmer rows={8} columns={8} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Top Action Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search visitors..."
            value={filters.search || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: '#6c757d', fontSize: 18 }} />,
            }}
            sx={{
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#f8f9fa',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                },
              }
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchVisitors}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ backgroundColor: '#6c757d', color: 'white', borderRadius: 2, p: 1.5, mr: 2 }}>
                  <Person />
                </Box>
                <Typography variant="h6" sx={{ color: '#333333', fontWeight: 500 }}>
                  Total Visitors
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ backgroundColor: '#4caf50', color: 'white', borderRadius: 2, p: 1.5, mr: 2 }}>
                  <Pending />
                </Box>
                <Typography variant="h6" sx={{ color: '#333333', fontWeight: 500 }}>
                  Active (In Store)
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ backgroundColor: '#2196f3', color: 'white', borderRadius: 2, p: 1.5, mr: 2 }}>
                  <CheckCircle />
                </Box>
                <Typography variant="h6" sx={{ color: '#333333', fontWeight: 500 }}>
                  Exited
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                {stats.exited}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Store</InputLabel>
              <Select
                value={filters.store_id}
                label="Store"
                onChange={(e) => setFilters(prev => ({ ...prev, store_id: e.target.value }))}
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small"
              type="date"
              label="Visit Date"
              value={filters.visit_date}
              onChange={(e) => setFilters(prev => ({ ...prev, visit_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active (In Store)</MenuItem>
                <MenuItem value="exited">Exited</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setFilters({
                  search: '',
                  store_id: 'all',
                  visit_date: new Date().toISOString().split('T')[0],
                  status: 'all',
                });
              }}
              fullWidth
              size="small"
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Visitors Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <TableCell>Visitor</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Visit Date</TableCell>
              <TableCell>Entry Time</TableCell>
              <TableCell>Exit Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Registered By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVisitors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    No visitors found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredVisitors.map((visitor) => (
                <TableRow key={visitor.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {visitor.photo_url ? (
                        <img
                          src={visitor.photo_url}
                          alt={visitor.name}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <ProfileAvatar
                          firstName={visitor.name.split(' ')[0]}
                          sx={{ bgcolor: '#6c757d', width: 40, height: 40 }}
                        />
                      )}
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {visitor.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{visitor.contact_number}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {visitor.store?.name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {visitor.reason || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(visitor.visit_date)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDateTime(visitor.entry_time)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {visitor.exit_time ? formatDateTime(visitor.exit_time) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={visitor.exit_time ? <CheckCircle /> : <Pending />}
                      label={visitor.exit_time ? 'Exited' : 'Active'}
                      color={visitor.exit_time ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {visitor.registrar?.first_name} {visitor.registrar?.last_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {visitor.registrar?.employee_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(visitor)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Visitor Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Visitor Details</DialogTitle>
        <DialogContent>
          {selectedVisitor && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sx={{ textAlign: 'center', mb: 2 }}>
                {selectedVisitor.photo_url ? (
                  <img
                    src={selectedVisitor.photo_url}
                    alt={selectedVisitor.name}
                    style={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <ProfileAvatar
                    firstName={selectedVisitor.name.split(' ')[0]}
                    sx={{ bgcolor: '#6c757d', width: 120, height: 120, mx: 'auto', fontSize: 48 }}
                  />
                )}
                <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
                  {selectedVisitor.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Contact Number</Typography>
                <Typography variant="body1">{selectedVisitor.contact_number}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Store</Typography>
                <Typography variant="body1">{selectedVisitor.store?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Reason</Typography>
                <Typography variant="body1">{selectedVisitor.reason || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Visit Date</Typography>
                <Typography variant="body1">{formatDate(selectedVisitor.visit_date)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Entry Time</Typography>
                <Typography variant="body1">{formatDateTime(selectedVisitor.entry_time)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Exit Time</Typography>
                <Typography variant="body1">
                  {selectedVisitor.exit_time ? formatDateTime(selectedVisitor.exit_time) : 'Not exited yet'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Registered By</Typography>
                <Typography variant="body1">
                  {selectedVisitor.registrar?.first_name} {selectedVisitor.registrar?.last_name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedVisitor.registrar?.employee_id}
                </Typography>
              </Grid>
              {selectedVisitor.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                  <Typography variant="body1">{selectedVisitor.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisitorsPage;

